/**
 * `useSimulation` — catálogo de escenarios y progreso de ejecuciones.
 *
 * El progreso se alimenta de los eventos SSE `sim.progress` / `sim.log` que el
 * backend publica durante la ejecución (el `start` solo devuelve el `runId`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  NocSimLogPayload,
  NocSimProgressPayload,
  NocSimScenario,
} from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import type { NocStreamRecord } from '@/features/noc/hooks/useNocStream';
import { simApi } from '../api/simApi';

export interface ScenarioRunView {
  runId: string;
  scenarioId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  step: number;
  totalSteps: number;
  percent: number;
  label?: string;
  error?: string;
  logs: string[];
  startedAt: number;
}

const MAX_LOGS_PER_RUN = 50;
const MAX_RUNS = 8;

export function useSimulation(streamEvents: NocStreamRecord[]): {
  catalog: NocSimScenario[];
  catalogLoading: boolean;
  catalogError: string | null;
  reloadCatalog: () => void;
  runs: ScenarioRunView[];
  startingId: string | null;
  startScenario: (scenarioId: string) => Promise<void>;
} {
  const [catalog, setCatalog] = useState<NocSimScenario[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [runs, setRuns] = useState<ScenarioRunView[]>([]);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [catalogKey, setCatalogKey] = useState(0);
  const lastSeqRef = useRef(0);

  useEffect(() => {
    let active = true;
    setCatalogLoading(true);
    void (async () => {
      try {
        const response = await simApi.catalog();
        if (active) {
          setCatalog(response.scenarios);
          setCatalogError(null);
        }
      } catch (caught) {
        if (active) {
          setCatalogError(
            caught instanceof ApiError ? caught.message : 'No se pudo cargar el catálogo.',
          );
        }
      } finally {
        if (active) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [catalogKey]);

  // Procesa solo los eventos nuevos del stream.
  useEffect(() => {
    const fresh = streamEvents.filter((record) => record.seq > lastSeqRef.current);
    if (fresh.length === 0) {
      return;
    }
    const last = streamEvents[streamEvents.length - 1];
    lastSeqRef.current = last ? last.seq : lastSeqRef.current;

    for (const record of fresh) {
      if (record.type === 'sim.progress') {
        const payload = record.data as NocSimProgressPayload;
        if (!payload?.runId) continue;
        setRuns((previous) =>
          previous.map((run) =>
            run.runId === payload.runId
              ? {
                  ...run,
                  status: payload.status,
                  step: payload.step,
                  totalSteps: payload.totalSteps,
                  percent: payload.percent,
                  label: payload.label ?? run.label,
                  error: payload.error ?? run.error,
                }
              : run,
          ),
        );
      } else if (record.type === 'sim.log') {
        const payload = record.data as NocSimLogPayload;
        if (!payload?.runId) continue;
        setRuns((previous) =>
          previous.map((run) =>
            run.runId === payload.runId
              ? { ...run, logs: [...run.logs, payload.message].slice(-MAX_LOGS_PER_RUN) }
              : run,
          ),
        );
      }
    }
  }, [streamEvents]);

  const reloadCatalog = useCallback(() => setCatalogKey((key) => key + 1), []);

  const startScenario = useCallback(async (scenarioId: string) => {
    setStartingId(scenarioId);
    try {
      const summary = await simApi.startScenario(scenarioId);
      setRuns((previous) =>
        [
          {
            runId: summary.runId,
            scenarioId: summary.scenarioId,
            status: 'RUNNING' as const,
            step: 0,
            totalSteps: summary.steps,
            percent: 0,
            logs: [],
            startedAt: Date.now(),
          },
          ...previous,
        ].slice(0, MAX_RUNS),
      );
    } finally {
      setStartingId(null);
    }
  }, []);

  return {
    catalog,
    catalogLoading,
    catalogError,
    reloadCatalog,
    runs,
    startingId,
    startScenario,
  };
}
